/**
 * @zendir/ui - LimitsBar Component
 *
 * Visual limits indicator for telemetry monitoring and spacecraft health assessment.
 * Displays a horizontal bar with colored zones (red-low, yellow-low, green, yellow-high, red-high)
 * and a marker showing the current value position within the range.
 *
 * Supports:
 * - 5-zone limits (red-low, yellow-low, green/normal, yellow-high, red-high)
 * - 3-zone limits (low, nominal, high)
 * - Blue zone for "out of range" / stale
 * - Animated value transitions
 * - Compact and full display modes
 * - Value label with units
 * - Configurable thresholds
 *
 * Astro UX Compliance:
 * - Uses official Astro status colors
 * - Consistent with Astro monitoring patterns
 *
 * @example
 * ```tsx
 * <LimitsBar
 *   value={72.5}
 *   redLow={10} yellowLow={25} greenLow={40}
 *   greenHigh={80} yellowHigh={90} redHigh={100}
 *   label="Temperature" unit="°C"
 * />
 * ```
 */

import React, { useMemo, memo } from "react";
import { useTheme } from "../../theme";

// =============================================================================
// Types
// =============================================================================

export type LimitsState =
  | "red_low"
  | "yellow_low"
  | "green"
  | "yellow_high"
  | "red_high"
  | "blue"
  | "stale";

export interface LimitsBarProps {
  /** Current value */
  value: number;
  /** Red low threshold (minimum critical) */
  redLow?: number;
  /** Yellow low threshold (minimum warning) */
  yellowLow?: number;
  /** Green low threshold (start of nominal) */
  greenLow?: number;
  /** Green high threshold (end of nominal) */
  greenHigh?: number;
  /** Yellow high threshold (maximum warning) */
  yellowHigh?: number;
  /** Red high threshold (maximum critical) */
  redHigh?: number;
  /** Overall min for the bar (defaults to redLow - 10%) */
  min?: number;
  /** Overall max for the bar (defaults to redHigh + 10%) */
  max?: number;
  /** Label text */
  label?: string;
  /** Unit string */
  unit?: string;
  /** Whether the value is stale / not updating */
  stale?: boolean;
  /** Compact mode (thinner bar, less text) */
  compact?: boolean;
  /** Show the numeric value on the bar */
  showValue?: boolean;
  /** Show limit threshold labels */
  showThresholds?: boolean;
  /** Width (default: '100%') */
  width?: number | string;
  /** Height of the bar (default: 20 for normal, 12 for compact) */
  barHeight?: number;
  /** Custom status override (forces a specific status regardless of value) */
  statusOverride?: LimitsState;
  /** Called when status changes */
  onStatusChange?: (status: LimitsState, value: number) => void;
  /** When false, lower-limit zones (red-low, yellow-low, green-low) are hidden even if threshold values are provided */
  showLower?: boolean;
  /** When false, upper-limit zones (green-high, yellow-high, red-high) are hidden even if threshold values are provided */
  showUpper?: boolean;
  /** CSS class */
  className?: string;
}

// =============================================================================
// Helpers
// =============================================================================

function getLimitsState(
  value: number,
  redLow?: number,
  yellowLow?: number,
  greenLow?: number,
  greenHigh?: number,
  yellowHigh?: number,
  redHigh?: number,
): LimitsState {
  if (redLow !== undefined && value < redLow) return "red_low";
  if (yellowLow !== undefined && value < yellowLow) return "yellow_low";
  if (greenLow !== undefined && value < greenLow) return "yellow_low";
  if (redHigh !== undefined && value > redHigh) return "red_high";
  if (yellowHigh !== undefined && value > yellowHigh) return "yellow_high";
  if (greenHigh !== undefined && value > greenHigh) return "yellow_high";
  return "green";
}

// =============================================================================
// Component
// =============================================================================

export const LimitsBar = memo(function LimitsBar({
  value,
  redLow: redLowProp,
  yellowLow: yellowLowProp,
  greenLow: greenLowProp,
  greenHigh: greenHighProp,
  yellowHigh: yellowHighProp,
  redHigh: redHighProp,
  min: minProp,
  max: maxProp,
  label,
  unit,
  stale = false,
  compact = false,
  showValue = true,
  showThresholds = false,
  showLower = true,
  showUpper = true,
  width = "100%",
  barHeight: barHeightProp,
  statusOverride,
  className = "",
}: LimitsBarProps): React.ReactElement {
  // Mask thresholds based on showLower / showUpper flags
  const redLow = showLower ? redLowProp : undefined;
  const yellowLow = showLower ? yellowLowProp : undefined;
  const greenLow = showLower ? greenLowProp : undefined;
  const greenHigh = showUpper ? greenHighProp : undefined;
  const yellowHigh = showUpper ? yellowHighProp : undefined;
  const redHigh = showUpper ? redHighProp : undefined;
  const { tokens } = useTheme();
  const softStatusColors = useMemo(
    () => ({
      normal: tokens.colors.status.normal,
      caution: tokens.colors.status.caution,
      serious: tokens.colors.status.serious,
      critical: tokens.colors.status.critical,
      off: tokens.colors.status.off,
      standby: tokens.colors.status.standby,
    }),
    [tokens],
  );

  const barHeight = barHeightProp ?? (compact ? 12 : 20);

  const _thresholds = useMemo(() => {
    const rl = redLow ?? -Infinity;
    const yl = yellowLow ?? redLow ?? -Infinity;
    const gl = greenLow ?? yellowLow ?? redLow ?? -Infinity;
    const gh = greenHigh ?? yellowHigh ?? redHigh ?? Infinity;
    const yh = yellowHigh ?? redHigh ?? Infinity;
    const rh = redHigh ?? Infinity;
    return { rl, yl, gl, gh, yh, rh };
  }, [redLow, yellowLow, greenLow, greenHigh, yellowHigh, redHigh]);

  const rangeMin =
    minProp ??
    (redLow !== undefined
      ? redLow - (redHigh !== undefined ? (redHigh - redLow) * 0.1 : 10)
      : 0);
  const rangeMax =
    maxProp ??
    (redHigh !== undefined
      ? redHigh + (redLow !== undefined ? (redHigh - redLow) * 0.1 : 10)
      : 100);
  const range = rangeMax - rangeMin;

  const status = stale
    ? "stale"
    : (statusOverride ??
      getLimitsState(
        value,
        redLow,
        yellowLow,
        greenLow,
        greenHigh,
        yellowHigh,
        redHigh,
      ));

  const statusColors: Record<LimitsState, string> = {
    red_low: softStatusColors.critical,
    red_high: softStatusColors.critical,
    yellow_low: softStatusColors.caution,
    yellow_high: softStatusColors.caution,
    green: softStatusColors.normal,
    blue: softStatusColors.standby,
    stale: softStatusColors.off,
  };

  const valuePercent =
    range > 0
      ? Math.max(0, Math.min(100, ((value - rangeMin) / range) * 100))
      : 50;

  // Build zone segments
  const zones = useMemo(() => {
    const segs: { start: number; end: number; color: string }[] = [];
    const toPercent = (v: number) =>
      Math.max(0, Math.min(100, ((v - rangeMin) / range) * 100));

    const critical = softStatusColors.critical;
    const caution = softStatusColors.caution;
    const normal = softStatusColors.normal;

    if (redLow !== undefined) {
      segs.push({ start: 0, end: toPercent(redLow), color: `${critical}66` });
    }
    if (yellowLow !== undefined) {
      const s = redLow !== undefined ? toPercent(redLow) : 0;
      segs.push({ start: s, end: toPercent(yellowLow), color: `${caution}66` });
    }
    {
      const s =
        yellowLow !== undefined
          ? toPercent(yellowLow)
          : redLow !== undefined
            ? toPercent(redLow)
            : 0;
      const e =
        yellowHigh !== undefined
          ? toPercent(yellowHigh)
          : redHigh !== undefined
            ? toPercent(redHigh)
            : 100;
      segs.push({ start: s, end: e, color: `${normal}44` });
    }
    if (yellowHigh !== undefined) {
      const e = redHigh !== undefined ? toPercent(redHigh) : 100;
      segs.push({
        start: toPercent(yellowHigh),
        end: e,
        color: `${caution}66`,
      });
    }
    if (redHigh !== undefined) {
      segs.push({
        start: toPercent(redHigh),
        end: 100,
        color: `${critical}66`,
      });
    }

    return segs;
  }, [
    redLow,
    yellowLow,
    yellowHigh,
    redHigh,
    rangeMin,
    range,
    softStatusColors,
  ]);

  const markerColor = statusColors[status];

  return (
    <div
      className={`limits-bar ${className}`}
      role="meter"
      aria-valuenow={value}
      aria-valuemin={rangeMin}
      aria-valuemax={rangeMax}
      aria-label={
        label
          ? `${label}: ${value}${unit ? " " + unit : ""}`
          : `${value}${unit ? " " + unit : ""}`
      }
      style={{ width, fontFamily: tokens.typography.fontFamily.primary }}
    >
      {/* Label row */}
      {(label || showValue) && !compact && (
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            marginBottom: tokens.spacing.xs,
            fontSize: tokens.typography.fontSize.sm,
          }}
        >
          {label && (
            <span
              style={{
                color: tokens.colors.text.secondary,
                fontWeight: tokens.typography.fontWeight.medium,
              }}
            >
              {label}
            </span>
          )}
          {showValue && (
            <span
              style={{
                color: markerColor,
                fontWeight: tokens.typography.fontWeight.medium,
                fontFamily: tokens.typography.fontFamily.mono,
              }}
            >
              {typeof value === "number" ? value.toFixed(2) : value}
              {unit && (
                <span
                  style={{
                    fontSize: tokens.typography.fontSize.xxs,
                    marginLeft: tokens.spacing.xxs,
                    color: tokens.colors.text.muted,
                  }}
                >
                  {unit}
                </span>
              )}
            </span>
          )}
        </div>
      )}

      {/* Bar */}
      <div
        style={{
          position: "relative",
          height: barHeight,
          borderRadius: tokens.borderRadius.full,
          background: tokens.colors.background.base,
          border: `1px solid ${tokens.colors.border.muted}`,
          overflow: "hidden",
        }}
      >
        {/* Zone segments */}
        {zones.map((z, i) => (
          <div
            key={i}
            style={{
              position: "absolute",
              left: `${z.start}%`,
              width: `${z.end - z.start}%`,
              height: "100%",
              background: z.color,
            }}
          />
        ))}

        {/* Value marker */}
        <div
          style={{
            position: "absolute",
            left: `${valuePercent}%`,
            top: 0,
            bottom: 0,
            width: compact ? 3 : 4,
            marginLeft: compact ? -1.5 : -2,
            background: markerColor,
            borderRadius: tokens.borderRadius.sm,
            boxShadow: `0 0 8px ${markerColor}66, 0 0 3px ${markerColor}44`,
            transition:
              "left 300ms cubic-bezier(0.4, 0, 0.2, 1), background 300ms ease",
          }}
        />

        {/* Compact label */}
        {compact && showValue && (
          <div
            style={{
              position: "absolute",
              right: tokens.spacing.xs,
              top: "50%",
              transform: "translateY(-50%)",
              fontSize: tokens.typography.fontSize.micro,
              fontWeight: tokens.typography.fontWeight.medium,
              color: markerColor,
              fontFamily: tokens.typography.fontFamily.mono,
              textShadow: `0 0 4px ${tokens.colors.background.base}`,
            }}
          >
            {typeof value === "number" ? value.toFixed(1) : value}
            {unit || ""}
          </div>
        )}
      </div>

      {/* Threshold labels */}
      {showThresholds && !compact && (
        <div
          style={{
            position: "relative",
            height: 14,
            marginTop: tokens.spacing.xxs,
          }}
        >
          {[
            { val: redLow, color: softStatusColors.critical },
            { val: yellowLow, color: softStatusColors.caution },
            { val: greenHigh ?? yellowHigh, color: softStatusColors.caution },
            { val: redHigh, color: softStatusColors.critical },
          ]
            .filter((t) => t.val !== undefined)
            .map((t, i) => {
              const pct = range > 0 ? ((t.val! - rangeMin) / range) * 100 : 0;
              return (
                <span
                  key={i}
                  aria-label={`Threshold: ${t.val}`}
                  style={{
                    position: "absolute",
                    left: `${pct}%`,
                    transform: "translateX(-50%)",
                    fontSize: tokens.typography.fontSize.micro,
                    color: t.color,
                    fontFamily: tokens.typography.fontFamily.mono,
                  }}
                >
                  {t.val}
                </span>
              );
            })}
        </div>
      )}
    </div>
  );
});

export default LimitsBar;
